import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { ShieldAlert, Lock, AlertTriangle, Sparkles, Loader2 } from "lucide-react";

function GlassAccordion({ items, prefix }) {
  return (
    <Accordion type="single" collapsible className="space-y-3">
      {items.map((item, i) => (
        <AccordionItem
          key={`${prefix}-${i}`}
          value={`${prefix}-${i}`}
          className="glass-panel border-b-0 px-4"
          data-testid={`faq-item-${prefix}-${i}`}
        >
          <AccordionTrigger
            className="text-left text-sm font-semibold hover:no-underline py-4"
            data-testid={`faq-trigger-${prefix}-${i}`}
          >
            {item.q}
          </AccordionTrigger>
          <AccordionContent className="text-sm text-slate-600 pb-4 whitespace-pre-line">
            {item.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function FAQ() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/promoter/faq").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading briefing…
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-4" data-testid="promoter-faq-page">
      <div
        className="glass-panel p-3 flex items-center gap-2.5 text-white"
        style={{ background: "rgba(15,23,42,0.88)" }}
        data-testid="faq-confidential-banner"
      >
        <Lock className="h-4 w-4 text-amber-300 shrink-0" />
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold leading-relaxed">
          Confidential — do not share without permission
        </div>
      </div>

      <div
        className="glass-panel p-5 relative overflow-hidden fade-up"
        style={{ background: "rgba(254, 242, 242, 0.75)" }}
        data-testid="faq-do-not-approach"
      >
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-red-400/20 blur-2xl pointer-events-none" />
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <span className="font-display font-bold text-lg text-red-700 tracking-tight">
            DO NOT APPROACH
          </span>
        </div>
        <p className="text-sm text-slate-700 mt-2">Do NOT approach or promote ÜBBS to:</p>
        <ul className="mt-2 space-y-1.5 text-sm font-medium text-slate-800">
          {(data.do_not_approach || []).map((d) => (
            <li key={d}>❌ {d}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-red-700">
          ÜBBS is only for existing adult smokers &amp; nicotine users (18+).
        </p>
      </div>

      <div className="fade-up fade-up-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400 mb-2 px-1">
          Frequently asked questions
        </div>
        <GlassAccordion items={data.faqs || []} prefix="faq" />
      </div>

      <div className="fade-up fade-up-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-medium text-amber-700 mb-2 px-1">
          <AlertTriangle className="h-3.5 w-3.5" /> Communication rules
        </div>
        <GlassAccordion items={data.rules || []} prefix="rules" />
      </div>

      <div className="glass-panel p-4 fade-up fade-up-2" data-testid="faq-grooming">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400">
          <Sparkles className="h-3.5 w-3.5" /> Grooming
        </div>
        <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{data.grooming}</p>
      </div>

      <div
        className="glass-panel p-4 fade-up fade-up-3"
        style={{ background: "rgba(240, 253, 244, 0.75)" }}
        data-testid="faq-always-remember"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700">
          Always remember
        </div>
        <p className="text-xs font-semibold text-slate-800 mt-2 leading-relaxed uppercase tracking-wide whitespace-pre-line">
          {data.always_remember}
        </p>
      </div>
    </div>
  );
}
